import type { InternalAxiosRequestConfig } from 'axios';
import request from '@/services/base-service';
import type { ApiResponse } from '@/types/base';
import type {
  CoCauGiaiDoanItem,
  CoCauFeatureFlagsView,
  OrgTreeAtMocView,
  TaiCoCauBatchRequest,
  TaiCoCauRequest,
  TaiCoCauResult,
  BoPhanLichSuView,
  BoPhanTaiNgayView,
  NhanVienTaiNgayView,
  MocLichSuView,
  OrgChangeScheduleView,
  ChangeSnapshotView,
} from '@/types/coCauGiaiDoan';

const ORG_BASE_URL = '/v1';
const baseUrlApi = `${ORG_BASE_URL}/cocau-giai-doan`;

export const coCauGiaiDoanApi = {
  getLichSuNam: (nam: number) =>
    request.get<ApiResponse<CoCauGiaiDoanItem[]>>(`${baseUrlApi}/lich-su/nam/${nam}`, {
      authorization: true,
    } as InternalAxiosRequestConfig),

  getFeatureFlags: () =>
    request.get<ApiResponse<CoCauFeatureFlagsView>>(`${baseUrlApi}/feature-flags`, {
      authorization: true,
    } as InternalAxiosRequestConfig),

  getOrgTreeAtMoc: (nam: number, thang: number) =>
    request.get<ApiResponse<OrgTreeAtMocView>>(`${baseUrlApi}/org-tree/nam/${nam}/thang/${thang}`, {
      authorization: true,
    } as InternalAxiosRequestConfig),

  getOrgTreeByGiaiDoanId: (nam: number, idGiaiDoan: string) =>
    request.get<ApiResponse<OrgTreeAtMocView>>(
      `${baseUrlApi}/org-tree/nam/${nam}/giai-doan/${encodeURIComponent(idGiaiDoan)}`,
      { authorization: true } as InternalAxiosRequestConfig
    ),

  taiCoCau: (payload: TaiCoCauRequest) =>
    request.post<ApiResponse<TaiCoCauResult>>(`${baseUrlApi}/tai-co-cau`, payload, {
      authorization: true,
    } as InternalAxiosRequestConfig),

  /** @deprecated Dùng taiCoCau */
  taiCoCauBatch: (payload: TaiCoCauBatchRequest) =>
    request.post<ApiResponse<TaiCoCauResult>>(`${baseUrlApi}/tai-co-cau`, payload, {
      authorization: true,
    } as InternalAxiosRequestConfig),

  khoa: (id: string) =>
    request.post<ApiResponse<boolean>>(`${baseUrlApi}/khoa/${id}`, {}, {
      authorization: true,
    } as InternalAxiosRequestConfig),

  /** M1: Timeline thay đổi của một phòng ban */
  getBoPhanLichSu: (idBoPhan: string, tuNgay?: string, denNgay?: string) => {
    const params = new URLSearchParams();
    if (tuNgay) params.set('tuNgay', tuNgay);
    if (denNgay) params.set('denNgay', denNgay);
    const qs = params.toString() ? `?${params.toString()}` : '';
    return request.get<ApiResponse<BoPhanLichSuView[]>>(
      `${baseUrlApi}/bophan/${encodeURIComponent(idBoPhan)}/lich-su${qs}`,
      { authorization: true } as InternalAxiosRequestConfig
    );
  },

  /** Live: Cây cơ cấu hiện tại — dùng cho staging tái cơ cấu */
  getLiveOrgTree: () =>
    request.get<ApiResponse<OrgTreeAtMocView>>(
      `${baseUrlApi}/org-tree/hien-tai`,
      { authorization: true } as InternalAxiosRequestConfig
    ),

  /** M1: Cây tổ chức tại ngày cụ thể (changeId: phân biệt nhiều mốc cùng ngày) */
  getOrgTreeAtDate: (ngay: string, changeId?: string | null) => {
    const params = new URLSearchParams({ ngay });
    if (changeId) params.set('changeId', changeId);
    return request.get<ApiResponse<BoPhanTaiNgayView[]>>(
      `${baseUrlApi}/org-tree/tai-ngay?${params.toString()}`,
      { authorization: true } as InternalAxiosRequestConfig
    );
  },

  /** M2: Nhân viên và phân công tại ngày cụ thể */
  getNhanVienTaiNgay: (ngay: string, changeId?: string | null) => {
    const params = new URLSearchParams({ ngay });
    if (changeId) params.set('changeId', changeId);
    return request.get<ApiResponse<NhanVienTaiNgayView[]>>(
      `${baseUrlApi}/org-tree/tai-ngay/nhan-vien?${params.toString()}`,
      { authorization: true } as InternalAxiosRequestConfig
    );
  },

  /** M2: Danh sách mốc lịch sử cơ cấu gom theo ngày */
  getMocList: (tuNgay?: string, denNgay?: string) => {
    const params = new URLSearchParams();
    if (tuNgay) params.set('tuNgay', tuNgay);
    if (denNgay) params.set('denNgay', denNgay);
    const qs = params.toString() ? `?${params.toString()}` : '';
    return request.get<ApiResponse<MocLichSuView[]>>(
      `${baseUrlApi}/lich-su/moc-list${qs}`,
      { authorization: true } as InternalAxiosRequestConfig
    );
  },

  /** Snapshot BP/NV theo ChangeId (gồm bộ phận đã giải thể) */
  getChangeSnapshots: (changeId: string) =>
    request.get<ApiResponse<ChangeSnapshotView>>(
      `${baseUrlApi}/lich-su/change/${encodeURIComponent(changeId)}`,
      { authorization: true } as InternalAxiosRequestConfig
    ),

  /** Phase 2: Danh sách lịch thay đổi cơ cấu tương lai/pending */
  getOrgChangeSchedules: (status?: number, tuNgay?: string, denNgay?: string) => {
    const params = new URLSearchParams();
    if (status !== undefined && status !== null) params.set('status', String(status));
    if (tuNgay) params.set('tuNgay', tuNgay);
    if (denNgay) params.set('denNgay', denNgay);
    const qs = params.toString() ? `?${params.toString()}` : '';
    return request.get<ApiResponse<OrgChangeScheduleView[]>>(
      `${baseUrlApi}/lich-thay-doi${qs}`,
      { authorization: true } as InternalAxiosRequestConfig
    );
  },

  /** Phase 2: Xem trước cây cơ cấu tương lai theo ChangeId pending */
  getScheduledOrgTree: (changeId: string) =>
    request.get<ApiResponse<OrgTreeAtMocView>>(
      `${baseUrlApi}/lich-thay-doi/${encodeURIComponent(changeId)}/org-tree`,
      { authorization: true } as InternalAxiosRequestConfig
    ),

  /** Phase 2: Apply thủ công một lịch pending/failed */
  applyOrgChangeSchedule: (changeId: string) =>
    request.post<ApiResponse<OrgChangeScheduleView>>(
      `${baseUrlApi}/lich-thay-doi/${encodeURIComponent(changeId)}/apply`,
      {},
      { authorization: true } as InternalAxiosRequestConfig
    ),

  /** Phase 2: Hủy một lịch pending trước ngày hiệu lực */
  cancelOrgChangeSchedule: (changeId: string) =>
    request.post<ApiResponse<OrgChangeScheduleView>>(
      `${baseUrlApi}/lich-thay-doi/${encodeURIComponent(changeId)}/cancel`,
      {},
      { authorization: true } as InternalAxiosRequestConfig
    ),

  /** Phase 2: Apply lịch thay đổi đến hạn, dùng cho job/admin test */
  applyDueOrgChangeSchedules: (dueDate?: string, maxItems?: number) => {
    const params = new URLSearchParams();
    if (dueDate) params.set('dueDate', dueDate);
    if (maxItems !== undefined && maxItems !== null) params.set('maxItems', String(maxItems));
    const qs = params.toString() ? `?${params.toString()}` : '';
    return request.post<ApiResponse<OrgChangeScheduleView[]>>(
      `${baseUrlApi}/lich-thay-doi/apply-due${qs}`,
      {},
      { authorization: true } as InternalAxiosRequestConfig
    );
  },
};
